import { useEffect, useMemo, useState } from 'react'
import {
  FlaskConical,
  Play,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  AlertCircle,
  Check,
  RefreshCw,
  ChevronRight,
  Sliders,
  BarChart3,
  LineChart as LineChartIcon,
  Loader2,
} from 'lucide-react'
import {
  LineChart,
  Line,
  BarChart,
  Bar as RechartsBar,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useTradingStore } from '../hooks/useTradingStore'
import InstrumentSelector from '../components/InstrumentSelector'
import MultiBacktestHeatmap from '../components/MultiBacktestHeatmap'
import InstrumentChartModal from '../components/InstrumentChartModal'

type TabMode = 'backtest' | 'optimize' | 'analysis'

export default function Backtest() {
  const {
    selectedStrategyId,
    selectedPreset,
    activeStrategy,
    availableStrategies,
    backtestInstruments,
    backtestStatus,
    backtestProgress,
    backtestResults,
    backtestError,
    fetchBacktestInstruments,
    setBacktestConfig,
    setParamOverrides,
    runBacktest,
    clearBacktestResults,
    setRunMode,
    // Backtest History
    backtestHistory,
    selectedHistoryBacktestId,
    // Optimization
    optimizationStatus,
    optimizationProgress,
    optimizationResults,
    optimizationError,
    optimizationParamGrid,
    optimizationTotalCombinations,
    optimizationMethod,
    optunaConfig,
    fetchParamGrid,
    runOptimization,
    clearOptimizationResults,
    setOptimizationMethod,
    setOptunaConfig,
    optimizationInstruments,
    setOptimizationInstruments,
    // Market Analysis
    marketAnalysis,
    marketAnalysisLoading,
    marketAnalysisError,
    fetchMarketAnalysis,
    // Multi-backtest
    multiBacktestInstruments,
    multiBacktestStrategyPresets,
    paramOverrides,
    multiBacktestStatus,
    multiBacktestProgress,
    multiBacktestResults,
    multiBacktestError,
    multiBacktestWorkers,
    setMultiBacktestInstruments,
    setMultiBacktestWorkers,
    setMultiBacktestStrategyPresets,
    runMultiBacktest,
    clearMultiBacktestResults,
    fetchStrategies,
  } = useTradingStore()

  const [activeTab, setActiveTab] = useState<TabMode>('backtest')
  const [selectedInstrument, setSelectedInstrument] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [multiMode, setMultiMode] = useState(false)
  const [drillDownInstrument, setDrillDownInstrument] = useState<string | null>(null)
  const [editableParams, setEditableParams] = useState<Record<string, any>>({})

  // Sync editable params from preset when strategy changes
  useEffect(() => {
    if (activeStrategy?.preset_params) {
      setEditableParams({ ...activeStrategy.preset_params })
      setParamOverrides(null)
    }
  }, [activeStrategy?.strategy_id, activeStrategy?.preset_name, activeStrategy?.preset_params, setParamOverrides])

  // Market Analysis local state
  const [analysisViewMode, setAnalysisViewMode] = useState<'grouped' | 'flat'>('grouped')
  const [analysisSortBy, setAnalysisSortBy] = useState<'return' | 'drawdown' | 'volatility'>('return')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [chartInstrument, setChartInstrument] = useState<string | null>(null)

  // Track which param grid values are selected (key=param name, value=Set of selected indices)
  const [selectedGridValues, setSelectedGridValues] = useState<Record<string, Set<number>>>({})

  // Fetch instruments, strategies, and history on mount.
  // Only claim 'backtest' run mode when nothing is currently running — otherwise
  // we'd overwrite the real mode (e.g. a live run in progress), which previously
  // caused the top-bar status to flip to "Backtest - Running" just by clicking
  // this tab. fetchStrategies hydrates the true runMode from the backend.
  useEffect(() => {
    fetchBacktestInstruments()
    fetchStrategies().then(() => {
      if (useTradingStore.getState().strategyStatus !== 'running') {
        setRunMode('backtest')
      }
    })
  }, [fetchBacktestInstruments, fetchStrategies, setRunMode])

  // Auto-populate dates when instrument changes
  useEffect(() => {
    const instrument = backtestInstruments.find(i => i.id === selectedInstrument)
    if (instrument) {
      // Only set dates if they're available from the catalog
      if (instrument.startDate) setStartDate(instrument.startDate)
      if (instrument.endDate) setEndDate(instrument.endDate)
    }
  }, [selectedInstrument, backtestInstruments])

  // Set default instrument
  useEffect(() => {
    if (backtestInstruments.length > 0 && !selectedInstrument) {
      setSelectedInstrument(backtestInstruments[0].id)
    }
  }, [backtestInstruments, selectedInstrument])

  // Fetch param grid when switching to optimize tab or when the active (loaded) strategy changes
  useEffect(() => {
    if (activeTab === 'optimize' && activeStrategy) {
      fetchParamGrid()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the strategy identity, not the whole object
  }, [activeTab, activeStrategy?.strategy_id, activeStrategy?.preset_name, fetchParamGrid])

  // Fetch market analysis when switching to analysis tab
  useEffect(() => {
    if (activeTab === 'analysis') {
      fetchMarketAnalysis()
    }
  }, [activeTab, fetchMarketAnalysis])

  // Initialize param grid: pre-select only the value matching the preset's
  // default for each parameter. The user can then click to toggle additional
  // values into the optimization sweep. If the preset has no matching value
  // for a given parameter (or no preset is loaded), fall back to the first
  // value so the grid always has at least one selection per row.
  useEffect(() => {
    if (optimizationParamGrid) {
      const presetParams = activeStrategy?.preset_params ?? {}
      const initial: Record<string, Set<number>> = {}
      for (const [param, values] of Object.entries(optimizationParamGrid)) {
        const valueList = values as any[]
        const presetValue = (presetParams as Record<string, any>)[param]
        let matchIdx = valueList.findIndex(
          v => v === presetValue || String(v) === String(presetValue),
        )
        if (matchIdx < 0) matchIdx = 0
        initial[param] = new Set([matchIdx])
      }
      setSelectedGridValues(initial)
    }
  }, [optimizationParamGrid, activeStrategy?.preset_params])

  // Get current strategy
  const currentStrategy = availableStrategies.find(s => s.id === selectedStrategyId)

  // Multi-backtest strategy/preset helpers
  const toggleStrategyPreset = (strategyId: string, preset: string) => {
    const exists = multiBacktestStrategyPresets.some(
      sp => sp.strategy_id === strategyId && sp.preset === preset
    )
    if (exists) {
      setMultiBacktestStrategyPresets(
        multiBacktestStrategyPresets.filter(sp => !(sp.strategy_id === strategyId && sp.preset === preset))
      )
    } else {
      setMultiBacktestStrategyPresets([...multiBacktestStrategyPresets, { strategy_id: strategyId, preset }])
    }
  }

  const isPresetSelected = (strategyId: string, preset: string) =>
    multiBacktestStrategyPresets.some(sp => sp.strategy_id === strategyId && sp.preset === preset)

  const multiJobCount = multiMode
    ? (multiBacktestStrategyPresets.length || 1) * multiBacktestInstruments.length
    : 0

  // Toggle a param grid value selection
  const toggleGridValue = (param: string, index: number) => {
    setSelectedGridValues(prev => {
      const current = prev[param] || new Set<number>()
      const next = new Set(current)
      if (next.has(index)) {
        // Don't allow deselecting the last value
        if (next.size <= 1) return prev
        next.delete(index)
      } else {
        next.add(index)
      }
      return { ...prev, [param]: next }
    })
  }

  // Compute selected combination count
  const selectedCombinations = useMemo(() => {
    if (!optimizationParamGrid) return 0
    let total = 1
    for (const param of Object.keys(optimizationParamGrid)) {
      const selected = selectedGridValues[param]
      total *= selected ? selected.size : (optimizationParamGrid[param] as any[]).length
    }
    return total
  }, [optimizationParamGrid, selectedGridValues])

  // Build the filtered param grid from selections
  const buildFilteredGrid = (): Record<string, any[]> | undefined => {
    if (!optimizationParamGrid) return undefined
    const grid: Record<string, any[]> = {}
    for (const [param, values] of Object.entries(optimizationParamGrid)) {
      const allValues = values as any[]
      const selected = selectedGridValues[param]
      if (selected && selected.size < allValues.length) {
        grid[param] = allValues.filter((_, i) => selected.has(i))
      } else {
        grid[param] = allValues
      }
    }
    return grid
  }

  // Handle run backtest (single or multi)
  const handleRunBacktest = async () => {
    if (multiMode) {
      if (multiBacktestInstruments.length === 0) return
      runMultiBacktest()
    } else {
      if (!selectedInstrument || !startDate || !endDate) return
      setBacktestConfig({
        instrument: selectedInstrument,
        startDate,
        endDate,
      })
      setTimeout(() => {
        runBacktest()
      }, 100)
    }
  }

  // Handle run optimization
  const handleRunOptimization = async () => {
    if (optimizationInstruments.length === 0) return

    setBacktestConfig({
      instrument: optimizationInstruments[0],
      startDate,
      endDate,
    })

    const filteredGrid = buildFilteredGrid()
    setTimeout(() => {
      runOptimization(filteredGrid)
    }, 100)
  }

  // Auto-switch tab when a history item is loaded from the sidebar
  useEffect(() => {
    if (!selectedHistoryBacktestId) return
    const bt = backtestHistory.find(b => b.backtest_id === selectedHistoryBacktestId)
    if (bt) {
      setActiveTab(bt.result_type === 'optimization' ? 'optimize' : 'backtest')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react to the selection, not to history refreshes
  }, [selectedHistoryBacktestId])

  // Handle clear results
  const handleClear = () => {
    if (multiMode) {
      clearMultiBacktestResults()
      setDrillDownInstrument(null)
    } else {
      clearBacktestResults()
      setSelectedInstrument(backtestInstruments[0]?.id || '')
    }
  }

  // Check if results have walk-forward data
  const hasWalkForwardData = optimizationResults
    ? optimizationResults.some(r => r.out_of_sample_return !== undefined)
    : false

  // Sort optimization results — by OOS return when walk-forward, otherwise net_return
  const sortedOptResults = optimizationResults
    ? [...optimizationResults].sort((a, b) => {
        if (hasWalkForwardData) {
          return (b.out_of_sample_return ?? -Infinity) - (a.out_of_sample_return ?? -Infinity)
        }
        return b.net_return - a.net_return
      })
    : null

  // Get parameter column names (exclude result/meta columns)
  const resultColumns = [
    'net_return', 'max_drawdown', 'final_balance',
    'in_sample_return', 'out_of_sample_return', 'overfit_ratio',
    'trial_number', 'trades',
    'n_instruments', 'instruments_detail', 'sub_algo_name',
  ]
  const paramColumns = sortedOptResults && sortedOptResults.length > 0
    ? Object.keys(sortedOptResults[0]).filter(k => !resultColumns.includes(k))
    : []

  const isRunning = backtestStatus === 'running' || optimizationStatus === 'running' || multiBacktestStatus === 'running'

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlaskConical size={28} className="text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Backtest</h1>
            <p className="text-sm text-[#666]">Test your strategy against historical data</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'backtest' && (backtestResults || multiBacktestResults) && (
            <button
              onClick={handleClear}
              className="btn btn-outline flex items-center gap-2"
            >
              <RotateCcw size={16} />
              New Backtest
            </button>
          )}
          {activeTab === 'optimize' && optimizationResults && (
            <button
              onClick={clearOptimizationResults}
              className="btn btn-outline flex items-center gap-2"
            >
              <RotateCcw size={16} />
              New Optimization
            </button>
          )}
          {activeTab === 'analysis' && marketAnalysis && (
            <button
              onClick={() => fetchMarketAnalysis(true)}
              disabled={marketAnalysisLoading}
              className="btn btn-outline flex items-center gap-2"
            >
              <RefreshCw size={16} className={marketAnalysisLoading ? 'animate-spin' : ''} />
              Refresh Analysis
            </button>
          )}
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 p-1 bg-[#1a1a1a] rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('backtest')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'backtest'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
              : 'text-[#888] hover:text-white'
          }`}
        >
          <FlaskConical size={16} />
          Backtest
        </button>
        <button
          onClick={() => setActiveTab('optimize')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'optimize'
              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
              : 'text-[#888] hover:text-white'
          }`}
        >
          <Sliders size={16} />
          Optimize
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
            activeTab === 'analysis'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
              : 'text-[#888] hover:text-white'
          }`}
        >
          <BarChart3 size={16} />
          Analysis
        </button>
      </div>

      {/* ============================================================ */}
      {/* BACKTEST TAB */}
      {/* ============================================================ */}
      {activeTab === 'backtest' && (
        <>
          {/* Configuration Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Configuration</h2>
            </div>

            {/* Strategy Info */}
            <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg">
              <div className="text-xs text-[#666] uppercase mb-1">Selected Strategy</div>
              {currentStrategy ? (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{currentStrategy.name}</span>
                    <span className="text-[#666] mx-2">|</span>
                    <span className="text-blue-400">{selectedPreset}</span>
                  </div>
                  <span className="text-xs text-[#666]">
                    Configure in Settings
                  </span>
                </div>
              ) : (
                <div className="text-yellow-400 text-sm">
                  Please select a strategy in the Settings tab first
                </div>
              )}
            </div>

            {/* Global Settings (Timeframe & Position Size) */}
            {activeStrategy?.preset_params && (
              <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
                <div className="text-xs text-[#666] uppercase mb-2">Timeframe, Sizing &amp; Risk</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-2">
                  {/* Bar Type / Timeframe */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${editableParams.bar_type !== activeStrategy.preset_params!.bar_type ? 'text-yellow-400' : 'text-[#888]'}`}>bar_type</span>
                    <select
                      value={editableParams.bar_type ?? activeStrategy.preset_params!.bar_type ?? '30min'}
                      onChange={(e) => {
                        const updated = { ...editableParams, bar_type: e.target.value }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-24 text-xs font-mono bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        editableParams.bar_type !== activeStrategy.preset_params!.bar_type
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    >
                      <option value="1min">1min</option>
                      <option value="5min">5min</option>
                      <option value="10min">10min</option>
                      <option value="12min">12min</option>
                      <option value="15min">15min</option>
                      <option value="30min">30min</option>
                      {/* 45min omitted: NautilusTrader cannot encode it (MINUTE
                          steps must divide 60). See utils/utils_instruments.py. */}
                      <option value="1hour">1hour</option>
                      <option value="2hour">2hour</option>
                      <option value="4hour">4hour</option>
                      <option value="8hour">8hour</option>
                      <option value="12hour">12hour</option>
                      <option value="1day">1day</option>
                      <option value="2day">2day</option>
                    </select>
                  </div>

                  {/* Trade Size / Position Size */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${(editableParams.trade_size ?? 1) !== (activeStrategy.preset_params!.trade_size ?? 1) ? 'text-yellow-400' : 'text-[#888]'}`}>trade_size</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={editableParams.trade_size ?? 1}
                      onChange={(e) => {
                        const newVal = e.target.value === '' ? '' : Number(e.target.value)
                        const updated = { ...editableParams, trade_size: newVal }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        (editableParams.trade_size ?? 1) !== (activeStrategy.preset_params!.trade_size ?? 1)
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    />
                  </div>

                  {/* Stop Loss % / Take Profit % — rendered ONLY when the loaded
                      strategy actually declares them. These used to render
                      unconditionally with `?? 2.5` / `?? 5.0` fallbacks, which
                      invented a stop and a target for configs that have neither;
                      touching one then injected an unknown key and the run failed
                      with "Invalid params_override: 'stop_loss_percent' — unknown".
                      Strategies that size risk differently (algo54 uses day-ATR
                      fractions, and has no take-profit at all) must not be shown
                      percent-of-price controls they cannot honour. */}
                  {'stop_loss_percent' in activeStrategy.preset_params! && (
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs ${editableParams.stop_loss_percent !== activeStrategy.preset_params!.stop_loss_percent ? 'text-yellow-400' : 'text-[#888]'}`}>stop_loss %</span>
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={editableParams.stop_loss_percent ?? activeStrategy.preset_params!.stop_loss_percent}
                        onChange={(e) => {
                          const newVal = e.target.value === '' ? '' : Number(e.target.value)
                          const updated = { ...editableParams, stop_loss_percent: newVal }
                          setEditableParams(updated)
                          const diff: Record<string, any> = {}
                          for (const [k, v] of Object.entries(updated)) {
                            if (v !== activeStrategy.preset_params![k]) diff[k] = v
                          }
                          setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                        }}
                        className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                          editableParams.stop_loss_percent !== activeStrategy.preset_params!.stop_loss_percent
                            ? 'border-yellow-500/50 text-yellow-300'
                            : 'border-[#333] text-white'
                        }`}
                      />
                    </div>
                  )}

                  {'take_profit_percent' in activeStrategy.preset_params! && (
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs ${editableParams.take_profit_percent !== activeStrategy.preset_params!.take_profit_percent ? 'text-yellow-400' : 'text-[#888]'}`}>take_profit %</span>
                      <input
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={editableParams.take_profit_percent ?? activeStrategy.preset_params!.take_profit_percent}
                        onChange={(e) => {
                          const newVal = e.target.value === '' ? '' : Number(e.target.value)
                          const updated = { ...editableParams, take_profit_percent: newVal }
                          setEditableParams(updated)
                          const diff: Record<string, any> = {}
                          for (const [k, v] of Object.entries(updated)) {
                            if (v !== activeStrategy.preset_params![k]) diff[k] = v
                          }
                          setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                        }}
                        className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                          editableParams.take_profit_percent !== activeStrategy.preset_params!.take_profit_percent
                            ? 'border-yellow-500/50 text-yellow-300'
                            : 'border-[#333] text-white'
                        }`}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Strategy Parameters (Editable) */}
            {activeStrategy?.preset_params && Object.keys(activeStrategy.preset_params).length > 0 && (
              <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-[#666] uppercase">Strategy Parameters</div>
                  <button
                    onClick={() => {
                      setEditableParams({ ...activeStrategy.preset_params! })
                      setParamOverrides(null)
                    }}
                    className="text-xs text-[#555] hover:text-white transition-colors flex items-center gap-1"
                  >
                    <RotateCcw size={10} />
                    Reset to Preset
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {(() => {
                    const groups: Record<string, string[]> = {}
                    for (const key of Object.keys(editableParams)) {
                      let group = 'Shared'
                      if (key.endsWith('_a')) group = 'Side A'
                      else if (key.endsWith('_b')) group = 'Side B'
                      else if (key.startsWith('hmm_')) group = 'HMM'
                      else if (key.startsWith('bocpd_')) group = 'BOCPD'
                      else if (key.startsWith('vol_spike') || key.startsWith('vol_weighted')) group = 'Volume'
                      else if (key.startsWith('circuit_breaker')) group = 'Circuit Breaker'
                      else if (key.startsWith('kalman_')) group = 'Kalman'
                      else if (key.startsWith('loss_gate')) group = 'Loss Gate'
                      else if (key.startsWith('b1b_')) group = 'Fix Drift'
                      else if (key.startsWith('b1c_')) group = 'US-Open Fade'
                      else if (key.startsWith('filter_') || key.startsWith('vol_pctile')) group = 'Filters'
                      else if (key.startsWith('day_atr') || key === 'min_session_bars') group = 'Session'
                      else if (key.startsWith('size_mode') || key.startsWith('risk_per_trade')
                               || key.endsWith('_contracts') || key === 'capital_override') group = 'Sizing'
                      if (!groups[group]) groups[group] = []
                      groups[group].push(key)
                    }
                    return Object.entries(groups).map(([group, keys]) => (
                      <div key={group} className="min-w-0">
                        <div className="text-[10px] text-[#555] uppercase font-semibold mb-1.5 border-b border-[#2a2a2a] pb-1">{group}</div>
                        <div className="flex flex-col gap-1">
                          {keys.map((key) => {
                            const value = editableParams[key]
                            const presetValue = activeStrategy.preset_params![key]
                            const isModified = value !== presetValue
                            const options = activeStrategy.param_options?.[key]
                            return (
                              <div key={key} className="flex items-center justify-between gap-2">
                                <span className={`text-xs truncate ${isModified ? 'text-yellow-400' : 'text-[#888]'}`}>
                                  {key.replace(/_a$|_b$/, '')}
                                </span>
                                {typeof presetValue === 'boolean' ? (
                                <button
                                  onClick={() => {
                                    const newValue = !value
                                    const updated = { ...editableParams, [key]: newValue }
                                    setEditableParams(updated)
                                    const diff: Record<string, any> = {}
                                    for (const [k, v] of Object.entries(updated)) {
                                      if (v !== activeStrategy.preset_params![k]) {
                                        diff[k] = v
                                      }
                                    }
                                    setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                                  }}
                                  className={`w-20 text-xs font-mono text-right px-1.5 py-0.5 rounded border ${
                                    isModified ? 'border-yellow-500/50' : 'border-[#333]'
                                  } ${value ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}
                                >
                                  {value ? 'True' : 'False'}
                                </button>
                              ) : options ? (
                                <select
                                  value={String(value)}
                                  onChange={(e) => {
                                    // Options keep their JSON type (ib_oca_type stays a number).
                                    const newValue = options.find(o => String(o) === e.target.value) ?? e.target.value
                                    const updated = { ...editableParams, [key]: newValue }
                                    setEditableParams(updated)
                                    const diff: Record<string, any> = {}
                                    for (const [k, v] of Object.entries(updated)) {
                                      if (v !== activeStrategy.preset_params![k]) {
                                        diff[k] = v
                                      }
                                    }
                                    setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                                  }}
                                  className={`w-28 text-xs font-mono bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                                    isModified ? 'border-yellow-500/50 text-yellow-300' : 'border-[#333] text-white'
                                  }`}
                                >
                                  {/* A value outside the list (an older preset) stays selectable. */}
                                  {(options.some(o => o === value) ? options : [value, ...options]).map(o => (
                                    <option key={String(o)} value={String(o)}>{String(o)}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={typeof presetValue === 'number' ? 'number' : 'text'}
                                  step={typeof presetValue === 'number' && !Number.isInteger(presetValue) ? '0.1' : undefined}
                                  value={value}
                                  onChange={(e) => {
                                    const newValue = typeof presetValue === 'number'
                                      ? (e.target.value === '' ? '' : Number(e.target.value))
                                      : e.target.value
                                    const updated = { ...editableParams, [key]: newValue }
                                    setEditableParams(updated)
                                    const diff: Record<string, any> = {}
                                    for (const [k, v] of Object.entries(updated)) {
                                      if (v !== activeStrategy.preset_params![k]) {
                                        diff[k] = v
                                      }
                                    }
                                    setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                                  }}
                                  className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                                    isModified ? 'border-yellow-500/50 text-yellow-300' : 'border-[#333] text-white'
                                  }`}
                                />
                              )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            )}

            {/* Error Message */}
            {backtestError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-400">{backtestError}</span>
              </div>
            )}

            {/* Single / Multi toggle */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setMultiMode(false)}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  !multiMode
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50'
                    : 'text-[#888] hover:text-white border border-transparent'
                }`}
              >
                Single Instrument
              </button>
              <button
                onClick={() => setMultiMode(true)}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  multiMode
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                    : 'text-[#888] hover:text-white border border-transparent'
                }`}
              >
                Multi
              </button>
            </div>

            {multiMode ? (
              /* Multi mode: strategies + instruments */
              <>
                {/* Strategy/Preset Selection */}
                <div className="mb-4">
                  <label className="input-label">
                    Strategies / Presets
                    {multiBacktestStrategyPresets.length > 0 && (
                      <span className="text-teal-400 ml-2 font-normal">({multiBacktestStrategyPresets.length} selected)</span>
                    )}
                    {multiBacktestStrategyPresets.length === 0 && (
                      <span className="text-[#666] ml-2 font-normal">(using active strategy)</span>
                    )}
                  </label>
                  {/* Edited parameters are a diff against the loaded preset: they ride only on its chip. */}
                  {paramOverrides && Object.keys(paramOverrides).length > 0 && activeStrategy
                    && multiBacktestStrategyPresets.length > 0 && (
                    <div className="text-[11px] text-yellow-400 mb-1">
                      {multiBacktestStrategyPresets.some(
                        c => c.strategy_id === activeStrategy.strategy_id && c.preset === activeStrategy.preset_name
                      )
                        ? `Edited parameters apply to ${activeStrategy.preset_name} only`
                        : `Edited parameters belong to ${activeStrategy.preset_name}, which is not selected — they will not be used`}
                    </div>
                  )}
                  <div className="max-h-48 overflow-y-auto border border-[#333] rounded-lg bg-[#1a1a1a]">
                    {availableStrategies.length === 0 ? (
                      <div className="p-3 text-center text-[#666] text-xs">No strategies found</div>
                    ) : (
                      availableStrategies.map(strategy => (
                        <div key={strategy.id} className="border-b border-[#222] last:border-b-0">
                          <div className="px-3 py-1.5 text-xs font-medium text-[#888] bg-[#151515]">
                            {strategy.name}
                          </div>
                          <div className="flex flex-wrap gap-1 px-3 py-1.5">
                            {strategy.presets.map(preset => {
                              const selected = isPresetSelected(strategy.id, preset)
                              return (
                                <button
                                  key={preset}
                                  type="button"
                                  onClick={() => toggleStrategyPreset(strategy.id, preset)}
                                  disabled={multiBacktestStatus === 'running'}
                                  className={`px-2 py-0.5 rounded text-[11px] font-mono transition-all ${
                                    selected
                                      ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50'
                                      : 'bg-[#222] text-[#888] border border-[#333] hover:border-[#555]'
                                  }`}
                                >
                                  {preset}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Instruments */}
                <div className="mb-4">
                  <label className="input-label">Instruments</label>
                  <InstrumentSelector
                    instruments={backtestInstruments}
                    multi
                    selectedIds={multiBacktestInstruments}
                    onSelectionChange={setMultiBacktestInstruments}
                    disabled={multiBacktestStatus === 'running'}
                    marketAnalysis={marketAnalysis}
                  />
                </div>

                {/* Job count summary */}
                {multiBacktestInstruments.length > 0 && (
                  <div className="mb-4 text-xs text-[#888]">
                    {multiBacktestStrategyPresets.length > 0 ? (
                      <span>
                        <span className="text-teal-400">{multiBacktestStrategyPresets.length}</span> strategy combos ×{' '}
                        <span className="text-blue-400">{multiBacktestInstruments.length}</span> instruments ={' '}
                        <span className="text-white font-medium">{multiJobCount} backtests</span>
                      </span>
                    ) : (
                      <span>
                        Active strategy × <span className="text-blue-400">{multiBacktestInstruments.length}</span> instruments
                      </span>
                    )}
                  </div>
                )}

                {/* Multi Error */}
                {multiBacktestError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-red-400">{multiBacktestError}</span>
                  </div>
                )}

                {/* Run Multi-Backtest Button */}
                <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                  {/* Backtests run in separate worker processes (NautilusTrader's
                      engine is process-global, so threads cannot be used). More
                      workers = faster, at roughly 1-2 GB of RAM each. */}
                  <div className="flex items-center justify-between mb-3">
                    <label
                      htmlFor="multi-workers"
                      className="text-xs text-[#888]"
                      title="How many backtests run at once, each in its own worker process. Set 1 to run them one at a time; lower it if a family of large contracts strains memory."
                    >
                      Parallel runs
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        id="multi-workers"
                        type="number"
                        min={1}
                        max={16}
                        value={multiBacktestWorkers}
                        onChange={e => setMultiBacktestWorkers(Number(e.target.value))}
                        disabled={multiBacktestStatus === 'running'}
                        className="input w-16 py-1 text-sm text-right disabled:opacity-50"
                      />
                      <span className="text-[11px] text-[#555] w-24">
                        {multiBacktestWorkers === 1 ? 'sequential' : `~${Math.max(1, Math.ceil(multiJobCount / multiBacktestWorkers))} rounds`}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleRunBacktest}
                    disabled={
                      multiBacktestStatus === 'running' ||
                      multiBacktestInstruments.length === 0 ||
                      (!selectedStrategyId && multiBacktestStrategyPresets.length === 0)
                    }
                    className="btn btn-primary flex items-center gap-2 w-full justify-center"
                  >
                    {multiBacktestStatus === 'running' ? (
                      <>
                        <Activity size={16} className="animate-pulse" />
                        Running Multi-Backtest...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Run Multi-Backtest ({multiJobCount} backtest{multiJobCount !== 1 ? 's' : ''})
                      </>
                    )}
                  </button>
                </div>

                {/* Multi Progress */}
                {multiBacktestStatus === 'running' && multiBacktestProgress && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-[#888]">
                        Completed {multiBacktestProgress.currentIndex} of {multiBacktestProgress.totalInstruments}
                      </span>
                      <span className="text-white font-mono text-xs">
                        {multiBacktestProgress.currentStrategy && (
                          <span className="text-teal-400">{multiBacktestProgress.currentStrategy}/{multiBacktestProgress.currentPreset}</span>
                        )}
                        {multiBacktestProgress.currentStrategy && ' @ '}
                        {multiBacktestProgress.currentInstrument}
                      </span>
                    </div>
                    <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 transition-all duration-300"
                        style={{ width: `${Math.round((multiBacktestProgress.currentIndex / multiBacktestProgress.totalInstruments) * 100)}%` }}
                      />
                    </div>
                    {/* What is actually executing right now. Jobs finish out of
                        order, so "current" above is the one that just landed. */}
                    {multiBacktestProgress.runningInstruments?.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px]">
                        <Loader2 size={11} className="text-teal-400 animate-spin" />
                        <span className="text-[#666] mr-1">running</span>
                        {multiBacktestProgress.runningInstruments.slice(0, 10).map(inst => (
                          <span key={inst} className="font-mono text-[#999] bg-[#1e1e1e] px-1.5 py-0.5 rounded">
                            {inst.split('.')[0]}
                          </span>
                        ))}
                        {multiBacktestProgress.runningInstruments.length > 10 && (
                          <span className="text-[#555]">
                            +{multiBacktestProgress.runningInstruments.length - 10}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Completed jobs list */}
                    {Object.keys(multiBacktestProgress.completedResults).length > 0 && (
                      <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(multiBacktestProgress.completedResults).map(([key, metrics]) => (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1.5">
                              <Check size={12} className="text-green-400" />
                              <span className="font-mono text-[#ccc] truncate max-w-[280px]">{key}</span>
                            </span>
                            <span className={`font-mono ${(metrics as any).pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              ${((metrics as any).pnl || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        {Object.entries(multiBacktestProgress.errors).map(([key, err]) => (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1.5">
                              <AlertCircle size={12} className="text-red-400" />
                              <span className="font-mono text-[#888] truncate max-w-[200px]">{key}</span>
                            </span>
                            <span className="text-red-400 truncate max-w-[200px]">{err as string}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Single instrument mode (original) */
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="input-label">Instrument</label>
                    <InstrumentSelector
                      instruments={backtestInstruments}
                      value={selectedInstrument}
                      onChange={setSelectedInstrument}
                      disabled={backtestStatus === 'running'}
                      marketAnalysis={marketAnalysis}
                    />
                  </div>
                  <div>
                    <label className="input-label">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="input w-full"
                      disabled={backtestStatus === 'running'}
                    />
                  </div>
                  <div>
                    <label className="input-label">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="input w-full"
                      disabled={backtestStatus === 'running'}
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                  <button
                    onClick={handleRunBacktest}
                    disabled={
                      backtestStatus === 'running' ||
                      !selectedStrategyId ||
                      !selectedInstrument ||
                      !startDate ||
                      !endDate
                    }
                    className="btn btn-primary flex items-center gap-2 w-full justify-center"
                  >
                    {backtestStatus === 'running' ? (
                      <>
                        <Activity size={16} className="animate-pulse" />
                        Running Backtest...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Run Backtest
                      </>
                    )}
                  </button>
                </div>

                {backtestStatus === 'running' && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-[#888]">Progress</span>
                      <span className="text-white">{backtestProgress}%</span>
                    </div>
                    <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${backtestProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Results */}
          {backtestStatus === 'completed' && backtestResults && (
            <>
              {/* Success Banner */}
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2">
                <Check size={16} className="text-green-400" />
                <span className="text-sm text-green-400">
                  Backtest completed successfully - {backtestResults.totalTrades} trades executed
                </span>
              </div>

              {/* Metrics Summary */}
              <div className="grid grid-cols-4 gap-4">
                <MetricCard
                  title="Total P&L"
                  value={`$${backtestResults.pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  icon={backtestResults.pnl >= 0 ? TrendingUp : TrendingDown}
                  color={backtestResults.pnl >= 0 ? 'green' : 'red'}
                />
                <MetricCard
                  title="Win Rate"
                  value={`${backtestResults.winRate.toFixed(1)}%`}
                  icon={Target}
                  color={backtestResults.winRate >= 50 ? 'green' : 'yellow'}
                />
                <MetricCard
                  title="Max Drawdown"
                  value={`${Math.abs(backtestResults.maxDrawdown).toLocaleString('en-US', { minimumFractionDigits: 2 })} %`}
                  icon={TrendingDown}
                  color="red"
                />
                <MetricCard
                  title="Sharpe Ratio"
                  value={backtestResults.sharpeRatio?.toFixed(2) || 'N/A'}
                  icon={Activity}
                  color={backtestResults.sharpeRatio && backtestResults.sharpeRatio > 1 ? 'green' : 'yellow'}
                />
              </div>

              {/* Additional Stats */}
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Additional Statistics</h2>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <div className="text-xs text-[#666] uppercase">Total Trades</div>
                    <div className="text-xl font-bold text-white mt-1">
                      {backtestResults.totalTrades}
                      {backtestResults.openPositions > 0 && (
                        <span
                          title="Positions still open when the data ended. Not counted as trades, and their losses are not in the P&L."
                          className="text-amber-400 text-sm font-medium ml-2"
                        >
                          +{backtestResults.openPositions} open
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <div className="text-xs text-[#666] uppercase">Profit Factor</div>
                    <div className="text-xl font-bold text-white mt-1">
                      {backtestResults.profitFactor?.toFixed(2) || 'N/A'}
                    </div>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg">
                    <div className="text-xs text-[#666] uppercase">Backtest ID</div>
                    <div className="text-sm font-mono text-[#888] mt-1">{backtestResults.backtest_id}</div>
                  </div>
                </div>
              </div>

              {/* Equity Curve */}
              {backtestResults.equityCurve.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Equity Curve</h2>
                  </div>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={backtestResults.equityCurve}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="time" stroke="#666" fontSize={10} />
                        <YAxis stroke="#666" fontSize={10} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #2a2a2a',
                            borderRadius: '8px',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="equity"
                          stroke="#4488ff"
                          dot={false}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Trade History */}
              {backtestResults.trades.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h2 className="card-title">Trade History</h2>
                    <span className="text-xs text-[#666]">{backtestResults.trades.length} trades</span>
                  </div>
                  <div className="overflow-x-auto max-h-[400px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#111]">
                        <tr className="text-left text-[#666] border-b border-[#2a2a2a]">
                          <th className="py-2 px-3">Time</th>
                          <th className="py-2 px-3">Side</th>
                          <th className="py-2 px-3">Price</th>
                          <th className="py-2 px-3">Quantity</th>
                          <th className="py-2 px-3 text-right">P&L</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backtestResults.trades.map((trade, index) => (
                          <tr key={index} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]">
                            <td className="py-2 px-3 text-[#888]">
                              {new Date(trade.timestamp).toLocaleString()}
                            </td>
                            <td className={`py-2 px-3 font-medium ${
                              trade.side === 'BUY' ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {trade.side}
                            </td>
                            <td className="py-2 px-3 text-white">
                              ${trade.price.toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-white">
                              {trade.quantity}
                            </td>
                            <td className={`py-2 px-3 text-right font-medium ${
                              trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Multi-Backtest Results */}
          {multiBacktestStatus === 'completed' && multiBacktestResults && (
            <>
              {/* Success Banner */}
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2">
                <Check size={16} className="text-green-400" />
                <span className="text-sm text-green-400">
                  Multi-backtest completed — {multiBacktestResults.completed_count} of {multiBacktestResults.total_instruments} instruments
                  {multiBacktestResults.failed_count > 0 && (
                    <span className="text-yellow-400 ml-1">({multiBacktestResults.failed_count} failed)</span>
                  )}
                </span>
              </div>

              {/* Heatmap */}
              <MultiBacktestHeatmap
                data={multiBacktestResults}
                onRowClick={(_sid, _preset, inst) => setDrillDownInstrument(drillDownInstrument === inst ? null : inst)}
              />

              {/* Drill-down: show individual backtest results */}
              {drillDownInstrument && (() => {
                const row = multiBacktestResults.heatmap_data.find(r => r.instrument === drillDownInstrument)
                if (!row) return null
                return (
                  <div className="card border border-blue-500/30">
                    <div className="card-header flex items-center justify-between">
                      <h2 className="card-title">
                        <span className="font-mono text-blue-400">{drillDownInstrument}</span> — Detailed Results
                      </h2>
                      <button
                        onClick={() => setDrillDownInstrument(null)}
                        className="text-xs text-[#888] hover:text-white"
                      >
                        Close
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">P&L</div>
                        <div className={`text-xl font-bold mt-1 ${row.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${row.pnl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Win Rate</div>
                        <div className="text-xl font-bold text-white mt-1">{row.win_rate.toFixed(1)}%</div>
                      </div>
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Max Drawdown</div>
                        <div className="text-xl font-bold text-red-400 mt-1">{Math.abs(row.max_drawdown).toFixed(2)}%</div>
                      </div>
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Sharpe Ratio</div>
                        <div className="text-xl font-bold text-white mt-1">{row.sharpe_ratio?.toFixed(2) || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Total Trades</div>
                        <div className="text-xl font-bold text-white mt-1">{row.total_trades}</div>
                      </div>
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Profit Factor</div>
                        <div className="text-xl font-bold text-white mt-1">{row.profit_factor?.toFixed(2) || 'N/A'}</div>
                      </div>
                      <div className="p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="text-xs text-[#666] uppercase">Net Return</div>
                        <div className={`text-xl font-bold mt-1 ${row.net_return >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {row.net_return.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </>
          )}

          {/* Empty State */}
          {backtestStatus === 'idle' && !backtestResults && multiBacktestStatus !== 'completed' && multiBacktestStatus !== 'running' && (
            <div className="card">
              <div className="text-center py-12">
                <FlaskConical size={48} className="mx-auto text-[#333] mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Ready to Backtest</h3>
                <p className="text-sm text-[#666] max-w-md mx-auto">
                  Select an instrument and date range above, then click "Run Backtest" to test
                  your strategy against historical data.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============================================================ */}
      {/* OPTIMIZE TAB */}
      {/* ============================================================ */}
      {activeTab === 'optimize' && (
        <>
          {/* Configuration Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Optimization Configuration</h2>
            </div>

            {/* Strategy Info */}
            <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg">
              <div className="text-xs text-[#666] uppercase mb-1">Selected Strategy</div>
              {currentStrategy ? (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{currentStrategy.name}</span>
                    <span className="text-[#666] mx-2">|</span>
                    <span className="text-purple-400">{selectedPreset}</span>
                  </div>
                  <span className="text-xs text-[#666]">
                    Configure in Settings
                  </span>
                </div>
              ) : (
                <div className="text-yellow-400 text-sm">
                  Please select a strategy in the Settings tab first
                </div>
              )}
            </div>

            {/* Global Settings (Timeframe, Sizing & Risk) — shared with Backtest tab */}
            {activeStrategy?.preset_params && (
              <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
                <div className="text-xs text-[#666] uppercase mb-2">Timeframe, Sizing &amp; Risk</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-2">
                  {/* Bar Type */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${editableParams.bar_type !== activeStrategy.preset_params!.bar_type ? 'text-yellow-400' : 'text-[#888]'}`}>bar_type</span>
                    <select
                      value={editableParams.bar_type ?? activeStrategy.preset_params!.bar_type ?? '30min'}
                      onChange={(e) => {
                        const updated = { ...editableParams, bar_type: e.target.value }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-24 text-xs font-mono bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        editableParams.bar_type !== activeStrategy.preset_params!.bar_type
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    >
                      {/* 45min omitted: NautilusTrader cannot encode it (MINUTE steps must divide 60). */}
                      {['1min','5min','10min','12min','15min','30min','1hour','2hour','4hour','8hour','12hour','1day','2day'].map(tf => (
                        <option key={tf} value={tf}>{tf}</option>
                      ))}
                    </select>
                  </div>
                  {/* Trade Size */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${(editableParams.trade_size ?? 1) !== (activeStrategy.preset_params!.trade_size ?? 1) ? 'text-yellow-400' : 'text-[#888]'}`}>trade_size</span>
                    <input
                      type="number" min={1} step={1}
                      value={editableParams.trade_size ?? 1}
                      onChange={(e) => {
                        const newVal = e.target.value === '' ? '' : Number(e.target.value)
                        const updated = { ...editableParams, trade_size: newVal }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        (editableParams.trade_size ?? 1) !== (activeStrategy.preset_params!.trade_size ?? 1)
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    />
                  </div>
                  {/* Stop Loss % */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${(editableParams.stop_loss_percent ?? 2.5) !== (activeStrategy.preset_params!.stop_loss_percent ?? 2.5) ? 'text-yellow-400' : 'text-[#888]'}`}>stop_loss %</span>
                    <input
                      type="number" min={0} step={0.1}
                      value={editableParams.stop_loss_percent ?? activeStrategy.preset_params!.stop_loss_percent ?? 2.5}
                      onChange={(e) => {
                        const newVal = e.target.value === '' ? '' : Number(e.target.value)
                        const updated = { ...editableParams, stop_loss_percent: newVal }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        (editableParams.stop_loss_percent ?? 2.5) !== (activeStrategy.preset_params!.stop_loss_percent ?? 2.5)
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    />
                  </div>
                  {/* Take Profit % */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${(editableParams.take_profit_percent ?? 5.0) !== (activeStrategy.preset_params!.take_profit_percent ?? 5.0) ? 'text-yellow-400' : 'text-[#888]'}`}>take_profit %</span>
                    <input
                      type="number" min={0} step={0.1}
                      value={editableParams.take_profit_percent ?? activeStrategy.preset_params!.take_profit_percent ?? 5.0}
                      onChange={(e) => {
                        const newVal = e.target.value === '' ? '' : Number(e.target.value)
                        const updated = { ...editableParams, take_profit_percent: newVal }
                        setEditableParams(updated)
                        const diff: Record<string, any> = {}
                        for (const [k, v] of Object.entries(updated)) {
                          if (v !== activeStrategy.preset_params![k]) diff[k] = v
                        }
                        setParamOverrides(Object.keys(diff).length > 0 ? diff : null)
                      }}
                      className={`w-20 text-xs font-mono text-right bg-[#111] border rounded px-1.5 py-0.5 outline-none focus:border-blue-500 ${
                        (editableParams.take_profit_percent ?? 5.0) !== (activeStrategy.preset_params!.take_profit_percent ?? 5.0)
                          ? 'border-yellow-500/50 text-yellow-300'
                          : 'border-[#333] text-white'
                      }`}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Strategy Parameters */}
            {activeStrategy?.preset_params && Object.keys(activeStrategy.preset_params).length > 0 && (
              <div className="mb-4 p-3 bg-[#1a1a1a] rounded-lg border border-[#2a2a2a]">
                <div className="text-xs text-[#666] uppercase mb-2">Strategy Parameters</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {(() => {
                    const groups: Record<string, string[]> = {}
                    for (const key of Object.keys(activeStrategy.preset_params)) {
                      let group = 'Shared'
                      if (key.endsWith('_a')) group = 'Side A'
                      else if (key.endsWith('_b')) group = 'Side B'
                      else if (key.startsWith('hmm_')) group = 'HMM'
                      else if (key.startsWith('bocpd_')) group = 'BOCPD'
                      else if (key.startsWith('vol_spike') || key.startsWith('vol_weighted')) group = 'Volume'
                      else if (key.startsWith('circuit_breaker')) group = 'Circuit Breaker'
                      else if (key.startsWith('kalman_')) group = 'Kalman'
                      else if (key.startsWith('loss_gate')) group = 'Loss Gate'
                      else if (key.startsWith('b1b_')) group = 'Fix Drift'
                      else if (key.startsWith('b1c_')) group = 'US-Open Fade'
                      else if (key.startsWith('filter_') || key.startsWith('vol_pctile')) group = 'Filters'
                      else if (key.startsWith('day_atr') || key === 'min_session_bars') group = 'Session'
                      else if (key.startsWith('size_mode') || key.startsWith('risk_per_trade')
                               || key.endsWith('_contracts') || key === 'capital_override') group = 'Sizing'
                      if (!groups[group]) groups[group] = []
                      groups[group].push(key)
                    }
                    return Object.entries(groups).map(([group, keys]) => (
                      <div key={group} className="min-w-0">
                        <div className="text-[10px] text-[#555] uppercase font-semibold mb-1.5 border-b border-[#2a2a2a] pb-1">{group}</div>
                        <div className="flex flex-col gap-1">
                          {keys.map((key) => {
                            const value = activeStrategy.preset_params![key]
                            return (
                              <div key={key} className="flex items-center justify-between gap-2">
                                <span className="text-xs text-[#888] truncate">{key.replace(/_a$|_b$/, '')}</span>
                                <span className={`text-xs font-mono flex-shrink-0 ${
                                  typeof value === 'boolean' ? (value ? 'text-green-400' : 'text-red-400') : 'text-white'
                                }`}>
                                  {typeof value === 'boolean' ? (value ? 'True' : 'False') : typeof value === 'number' ? value : String(value)}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            )}

            {/* Optimization Method Toggle */}
            <div className="mb-4">
              <label className="input-label">Optimization Method</label>
              <div className="flex gap-1 p-1 bg-[#111] rounded-lg w-fit">
                <button
                  onClick={() => setOptimizationMethod('grid')}
                  disabled={isRunning}
                  className={`px-4 py-2 rounded-md text-sm transition-all ${
                    optimizationMethod === 'grid'
                      ? 'bg-purple-600 text-white'
                      : 'text-[#888] hover:text-white'
                  } disabled:cursor-not-allowed`}
                >
                  Grid Search (Exhaustive)
                </button>
                <button
                  onClick={() => setOptimizationMethod('optuna')}
                  disabled={isRunning}
                  className={`px-4 py-2 rounded-md text-sm transition-all ${
                    optimizationMethod === 'optuna'
                      ? 'bg-purple-600 text-white'
                      : 'text-[#888] hover:text-white'
                  } disabled:cursor-not-allowed`}
                >
                  Optuna (Smart Search)
                </button>
              </div>
            </div>

            {/* Optuna Settings */}
            {optimizationMethod === 'optuna' && (
              <div className="mb-4 p-4 bg-[#1a1a1a] rounded-lg border border-purple-500/20">
                <h3 className="text-sm font-medium text-purple-400 mb-3">Optuna Settings</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="input-label">Trials</label>
                    <input
                      type="number"
                      value={optunaConfig.n_trials}
                      onChange={(e) => setOptunaConfig({ n_trials: Math.max(10, parseInt(e.target.value) || 10) })}
                      min={10}
                      max={5000}
                      className="input w-full"
                      disabled={isRunning}
                    />
                    {selectedCombinations > 0 && (
                      <p className="text-xs text-[#555] mt-1">
                        Grid has {selectedCombinations} combos
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="input-label">Walk-Forward Splits</label>
                    <select
                      value={optunaConfig.walk_forward_splits}
                      onChange={(e) => setOptunaConfig({ walk_forward_splits: parseInt(e.target.value) })}
                      className="input w-full"
                      disabled={isRunning}
                    >
                      <option value={0}>Disabled</option>
                      <option value={2}>2 folds</option>
                      <option value={3}>3 folds</option>
                      <option value={5}>5 folds</option>
                    </select>
                  </div>
                  <div>
                    <label className="input-label">Train Ratio</label>
                    <select
                      value={optunaConfig.train_ratio}
                      onChange={(e) => setOptunaConfig({ train_ratio: parseFloat(e.target.value) })}
                      className="input w-full"
                      disabled={isRunning}
                    >
                      <option value={0.6}>60% / 40%</option>
                      <option value={0.7}>70% / 30%</option>
                      <option value={0.8}>80% / 20%</option>
                    </select>
                  </div>
                </div>
                {optunaConfig.walk_forward_splits > 0 && (
                  <div className="mt-3 p-2 bg-purple-500/5 border border-purple-500/20 rounded text-xs text-purple-300">
                    Walk-forward validation splits data into train/test periods to measure
                    out-of-sample performance. This helps prevent overfitting to historical data.
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {optimizationError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-red-400">{optimizationError}</span>
              </div>
            )}

            {/* Instrument Selection (multi-mode for cross-instrument optimization) */}
            <div className="mb-4">
              <label className="input-label">Instruments</label>
              <InstrumentSelector
                instruments={backtestInstruments}
                multi
                selectedIds={optimizationInstruments}
                onSelectionChange={setOptimizationInstruments}
                disabled={isRunning}
                marketAnalysis={marketAnalysis}
              />
              {optimizationInstruments.length > 0 && (
                <div className="mt-2 text-xs text-[#888]">
                  <span className="text-purple-400">{optimizationInstruments.length}</span> instrument{optimizationInstruments.length !== 1 ? 's' : ''} selected
                  {optimizationInstruments.length > 1 && (
                    <span className="text-[#555]"> — metrics will be averaged across instruments</span>
                  )}
                </div>
              )}
            </div>

            {/* Run Optimization Button */}
            <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
              <button
                onClick={handleRunOptimization}
                disabled={isRunning || !selectedStrategyId || optimizationInstruments.length === 0}
                className="btn flex items-center gap-2 w-full justify-center bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {optimizationStatus === 'running' ? (
                  <>
                    <Activity size={16} className="animate-pulse" />
                    Running {optimizationMethod === 'optuna' ? 'Optuna' : 'Grid'} Optimization...
                  </>
                ) : (
                  <>
                    <Sliders size={16} />
                    Run {optimizationMethod === 'optuna' ? 'Optuna' : 'Grid'} Optimization
                    <span className="text-purple-200 text-xs">
                      {optimizationMethod === 'optuna'
                        ? `(${optunaConfig.n_trials} trials${optimizationInstruments.length > 1 ? ` × ${optimizationInstruments.length} instruments` : ''})`
                        : selectedCombinations > 0 ? `(${selectedCombinations} combos${optimizationInstruments.length > 1 ? ` × ${optimizationInstruments.length} instruments` : ''})` : ''
                      }
                    </span>
                  </>
                )}
              </button>
            </div>

            {/* Progress */}
            {optimizationStatus === 'running' && optimizationProgress && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#888]">
                    {optimizationMethod === 'optuna' ? 'Trial' : 'Combination'}
                  </span>
                  <span className="text-white">
                    {optimizationProgress.current} / {optimizationProgress.total}
                    {optimizationProgress.total > 0 && (
                      <span className="text-[#666] ml-2">
                        ({Math.round((optimizationProgress.current / optimizationProgress.total) * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all duration-300"
                    style={{
                      width: optimizationProgress.total > 0
                        ? `${(optimizationProgress.current / optimizationProgress.total) * 100}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Running spinner (no progress yet) */}
            {optimizationStatus === 'running' && !optimizationProgress && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-[#888]">Starting optimization...</span>
                </div>
                <div className="h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 animate-pulse w-1/4" />
                </div>
              </div>
            )}
          </div>

          {/* Parameter Grid Display */}
          {optimizationParamGrid && Object.keys(optimizationParamGrid).length > 0 && (
            <div className="card">
              <div className="card-header">
                <div className="flex items-center gap-2">
                  <Sliders size={18} className="text-purple-400" />
                  <h2 className="card-title">Parameter Grid</h2>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#666]">
                    {selectedCombinations !== optimizationTotalCombinations ? (
                      <><span className="text-purple-400 font-medium">{selectedCombinations}</span> / {optimizationTotalCombinations} combinations</>
                    ) : (
                      <>{optimizationTotalCombinations} combinations</>
                    )}
                  </span>
                  <span className="text-xs text-[#555]">
                    Click to toggle{optimizationMethod === 'optuna' ? ' (defines search space)' : ''}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(optimizationParamGrid).map(([param, values]) => (
                  <div key={param} className="p-3 bg-[#1a1a1a] rounded-lg">
                    <div className="text-xs text-purple-400 font-medium mb-1">{param}</div>
                    <div className="flex flex-wrap gap-1">
                      {(values as any[]).map((v, i) => {
                        const isSelected = selectedGridValues[param]?.has(i) ?? true
                        return (
                          <button
                            key={i}
                            onClick={() => toggleGridValue(param, i)}
                            disabled={isRunning}
                            className={`text-xs px-2 py-0.5 rounded transition-all border ${
                              isSelected
                                ? 'bg-purple-500/20 text-purple-300 border-purple-500/50 hover:bg-purple-500/30'
                                : 'bg-[#1a1a1a] text-[#555] border-[#333] line-through hover:border-[#555]'
                            } disabled:cursor-not-allowed`}
                          >
                            {String(v)}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimization Results */}
          {optimizationStatus === 'completed' && sortedOptResults && sortedOptResults.length > 0 && (
            <>
              {/* Success Banner */}
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2">
                <Check size={16} className="text-green-400" />
                <span className="text-sm text-green-400">
                  Optimization completed - {sortedOptResults.length} {hasWalkForwardData ? 'trials' : 'combinations'} tested
                  {optimizationInstruments.length > 1 && ` across ${optimizationInstruments.length} instruments`}
                  {hasWalkForwardData && ' (walk-forward validated)'}
                </span>
              </div>

              {/* Results Table */}
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Optimization Results</h2>
                  <span className="text-xs text-[#666]">
                    Sorted by {hasWalkForwardData ? 'out-of-sample return' : 'net return'} (best first)
                  </span>
                </div>
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#111]">
                      <tr className="text-left text-[#666] border-b border-[#2a2a2a]">
                        <th className="py-2 px-3 text-center">#</th>
                        {paramColumns.map(col => (
                          <th key={col} className="py-2 px-3">{col}</th>
                        ))}
                        <th className="py-2 px-3 text-right">Net Return %</th>
                        <th className="py-2 px-3 text-right">Max Drawdown</th>
                        {!hasWalkForwardData && (
                          <th className="py-2 px-3 text-right">Final Balance</th>
                        )}
                        {hasWalkForwardData && (
                          <>
                            <th className="py-2 px-3 text-right">In-Sample %</th>
                            <th className="py-2 px-3 text-right">OOS %</th>
                            <th className="py-2 px-3 text-right">Overfit Ratio</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOptResults.map((result, index) => {
                        const overfitRatio = result.overfit_ratio as number | null | undefined
                        const overfitColor = overfitRatio != null
                          ? Math.abs(overfitRatio) <= 1.2 ? 'text-green-400'
                            : Math.abs(overfitRatio) <= 2.0 ? 'text-yellow-400'
                            : 'text-red-400'
                          : 'text-[#888]'
                        return (
                          <tr
                            key={index}
                            className={`border-b border-[#1a1a1a] hover:bg-[#1a1a1a] ${
                              index < 3 ? 'bg-green-500/5' : ''
                            }`}
                          >
                            <td className="py-2 px-3 text-center text-[#666]">
                              {index < 3 ? (
                                <span className="text-yellow-400 font-bold">{index + 1}</span>
                              ) : (
                                index + 1
                              )}
                            </td>
                            {paramColumns.map(col => (
                              <td key={col} className="py-2 px-3 text-white font-mono text-xs">
                                {String(result[col])}
                              </td>
                            ))}
                            <td className={`py-2 px-3 text-right font-bold ${
                              (result.net_return ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {result.net_return != null
                                ? `${result.net_return >= 0 ? '+' : ''}${result.net_return.toFixed(2)}%`
                                : '-'}
                            </td>
                            <td className="py-2 px-3 text-right text-red-400 font-mono text-xs">
                              {result.max_drawdown}
                            </td>
                            {!hasWalkForwardData && (
                              <td className="py-2 px-3 text-right text-white font-mono">
                                ${typeof result.final_balance === 'number'
                                  ? result.final_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })
                                  : result.final_balance}
                              </td>
                            )}
                            {hasWalkForwardData && (
                              <>
                                <td className={`py-2 px-3 text-right font-mono text-xs ${
                                  (result.in_sample_return ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {result.in_sample_return != null
                                    ? `${result.in_sample_return >= 0 ? '+' : ''}${result.in_sample_return.toFixed(2)}%`
                                    : '-'}
                                </td>
                                <td className={`py-2 px-3 text-right font-bold ${
                                  (result.out_of_sample_return ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {result.out_of_sample_return != null
                                    ? `${result.out_of_sample_return >= 0 ? '+' : ''}${result.out_of_sample_return.toFixed(2)}%`
                                    : '-'}
                                </td>
                                <td className={`py-2 px-3 text-right font-mono text-xs ${overfitColor}`}>
                                  {overfitRatio != null ? overfitRatio.toFixed(2) : '-'}
                                </td>
                              </>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Empty State */}
          {optimizationStatus === 'idle' && !optimizationResults && (
            <div className="card">
              <div className="text-center py-12">
                <Sliders size={48} className="mx-auto text-[#333] mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Ready to Optimize</h3>
                <p className="text-sm text-[#666] max-w-md mx-auto">
                  Select one or more instruments above, then click "Run Optimization" to find the best
                  parameter combinations for your strategy. Selecting multiple instruments
                  averages metrics across them, finding parameters that generalize well.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============================================================ */}
      {/* ANALYSIS TAB */}
      {/* ============================================================ */}
      {activeTab === 'analysis' && (
        <>
          {/* Loading State */}
          {marketAnalysisLoading && (
            <div className="card">
              <div className="text-center py-12">
                <Activity size={48} className="mx-auto text-emerald-400 animate-pulse mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Analyzing Markets...</h3>
                <p className="text-sm text-[#666]">
                  Computing metrics for all instruments. This may take a moment on first run.
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {marketAnalysisError && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-400">{marketAnalysisError}</span>
            </div>
          )}

          {/* Results */}
          {marketAnalysis && !marketAnalysisLoading && (() => {
            const sortedInstruments = [...marketAnalysis.all_instruments].sort((a, b) => {
              if (analysisSortBy === 'return') return b.total_return_pct - a.total_return_pct
              if (analysisSortBy === 'drawdown') return a.max_drawdown_pct - b.max_drawdown_pct
              return b.volatility_ann_pct - a.volatility_ann_pct
            })

            return (
              <>
                {/* Controls Bar */}
                <div className="card">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      {/* View Mode Toggle */}
                      <div className="flex gap-1 p-1 bg-[#111] rounded-lg">
                        <button
                          onClick={() => setAnalysisViewMode('grouped')}
                          className={`px-3 py-1.5 rounded-md text-xs transition-all ${
                            analysisViewMode === 'grouped'
                              ? 'bg-emerald-600 text-white'
                              : 'text-[#888] hover:text-white'
                          }`}
                        >
                          Grouped by Root
                        </button>
                        <button
                          onClick={() => setAnalysisViewMode('flat')}
                          className={`px-3 py-1.5 rounded-md text-xs transition-all ${
                            analysisViewMode === 'flat'
                              ? 'bg-emerald-600 text-white'
                              : 'text-[#888] hover:text-white'
                          }`}
                        >
                          All Instruments
                        </button>
                      </div>

                      {/* Sort By */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#666]">Sort by:</span>
                        <select
                          value={analysisSortBy}
                          onChange={(e) => setAnalysisSortBy(e.target.value as 'return' | 'drawdown' | 'volatility')}
                          className="input text-xs py-1"
                        >
                          <option value="return">Total Return %</option>
                          <option value="drawdown">Max Drawdown %</option>
                          <option value="volatility">Volatility %</option>
                        </select>
                      </div>
                    </div>

                    <div className="text-xs text-[#666]">
                      {marketAnalysis.all_instruments.length} instruments | Computed: {new Date(marketAnalysis.computed_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* Return Distribution Bar Chart */}
                <div className="card">
                  <div className="card-header">
                    <div className="flex items-center gap-2">
                      <BarChart3 size={18} className="text-emerald-400" />
                      <h2 className="card-title">Return Distribution</h2>
                    </div>
                    <span className="text-xs text-[#666]">
                      {sortedInstruments.filter(i => i.total_return_pct >= 0).length} bullish / {sortedInstruments.filter(i => i.total_return_pct < 0).length} bearish
                    </span>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    <div style={{ height: Math.max(200, sortedInstruments.length * 28) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={sortedInstruments} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" horizontal={false} />
                          <XAxis type="number" stroke="#666" fontSize={10} tickFormatter={(v: number) => `${v}%`} />
                          <YAxis type="category" dataKey="id" width={130} stroke="#666" fontSize={11} tick={{ fontFamily: 'monospace' }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px' }}
                            formatter={(value: number) => [`${value >= 0 ? '+' : ''}${value.toFixed(2)}%`, 'Return']}
                            labelStyle={{ color: '#888' }}
                          />
                          <ReferenceLine x={0} stroke="#444" strokeDasharray="3 3" />
                          <RechartsBar dataKey="total_return_pct" radius={[0, 4, 4, 0]}>
                            {sortedInstruments.map((entry, index) => (
                              <Cell key={index} fill={entry.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
                            ))}
                          </RechartsBar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* GROUPED VIEW */}
                {analysisViewMode === 'grouped' && (
                  <div className="space-y-3">
                    {Object.values(marketAnalysis.groups)
                      .sort((a, b) => b.avg_return_pct - a.avg_return_pct)
                      .map((group) => (
                        <div key={group.root_symbol} className="card">
                          {/* Group Header */}
                          <div
                            className="flex items-center justify-between cursor-pointer"
                            onClick={() => {
                              setExpandedGroups(prev => {
                                const next = new Set(prev)
                                next.has(group.root_symbol) ? next.delete(group.root_symbol) : next.add(group.root_symbol)
                                return next
                              })
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <ChevronRight
                                size={16}
                                className={`text-[#666] transition-transform ${
                                  expandedGroups.has(group.root_symbol) ? 'rotate-90' : ''
                                }`}
                              />
                              <span className="text-lg font-bold text-white">{group.root_symbol}</span>
                              <span className="text-xs text-[#666]">
                                {group.instruments.length} contract{group.instruments.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <div className="text-xs text-[#666]">Avg Return</div>
                                <div className={`text-sm font-bold ${group.avg_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {group.avg_return_pct >= 0 ? '+' : ''}{group.avg_return_pct.toFixed(2)}%
                                </div>
                              </div>
                              {group.most_bullish && (
                                <div className="text-right">
                                  <div className="text-xs text-[#666]">Most Bullish</div>
                                  <div className="text-sm text-green-400">
                                    {group.most_bullish.id.split('.')[0]}
                                    <span className="ml-1 font-bold">+{group.most_bullish.total_return_pct.toFixed(1)}%</span>
                                  </div>
                                </div>
                              )}
                              {group.most_bearish && (
                                <div className="text-right">
                                  <div className="text-xs text-[#666]">Most Bearish</div>
                                  <div className="text-sm text-red-400">
                                    {group.most_bearish.id.split('.')[0]}
                                    <span className="ml-1 font-bold">{group.most_bearish.total_return_pct.toFixed(1)}%</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Expanded: Instrument Table */}
                          {expandedGroups.has(group.root_symbol) && (
                            <div className="mt-3 pt-3 border-t border-[#2a2a2a]">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-[#666] border-b border-[#2a2a2a]">
                                    <th className="py-2 px-3">Contract</th>
                                    <th className="py-2 px-3">Period</th>
                                    <th className="py-2 px-3 text-right">Return %</th>
                                    <th className="py-2 px-3 text-right">Max DD %</th>
                                    <th className="py-2 px-3 text-right">Volatility %</th>
                                    <th className="py-2 px-3 text-center">Trend</th>
                                    <th className="py-2 px-3 text-right">Days</th>
                                    <th className="py-2 px-3"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...group.instruments]
                                    .sort((a, b) => b.total_return_pct - a.total_return_pct)
                                    .map((inst) => (
                                      <tr key={inst.id} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]">
                                        <td className="py-2 px-3 text-white font-mono text-xs">{inst.id}</td>
                                        <td className="py-2 px-3 text-[#888] text-xs">{inst.start_date} to {inst.end_date}</td>
                                        <td className={`py-2 px-3 text-right font-bold ${inst.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                          {inst.total_return_pct >= 0 ? '+' : ''}{inst.total_return_pct.toFixed(2)}%
                                        </td>
                                        <td className="py-2 px-3 text-right text-red-400 text-xs">{inst.max_drawdown_pct.toFixed(2)}%</td>
                                        <td className="py-2 px-3 text-right text-[#888] text-xs">{inst.volatility_ann_pct.toFixed(1)}%</td>
                                        <td className="py-2 px-3 text-center">
                                          <span className={`text-xs px-2 py-0.5 rounded ${
                                            inst.trend === 'bullish' ? 'bg-green-500/10 text-green-400'
                                              : inst.trend === 'bearish' ? 'bg-red-500/10 text-red-400'
                                              : 'bg-yellow-500/10 text-yellow-400'
                                          }`}>
                                            {inst.trend}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-right text-[#888] text-xs">{inst.trading_days}</td>
                                        <td className="py-2 px-3">
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                setChartInstrument(inst.id)
                                              }}
                                              className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                                              title="View price chart"
                                            >
                                              <LineChartIcon size={12} />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                setSelectedInstrument(inst.id)
                                                setActiveTab('backtest')
                                              }}
                                              className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                                            >
                                              Backtest
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* FLAT VIEW */}
                {analysisViewMode === 'flat' && (
                  <div className="card">
                    <div className="card-header">
                      <h2 className="card-title">All Instruments</h2>
                      <span className="text-xs text-[#666]">
                        Sorted by {analysisSortBy === 'return' ? 'total return' : analysisSortBy === 'drawdown' ? 'max drawdown' : 'volatility'}
                      </span>
                    </div>
                    <div className="overflow-x-auto max-h-[600px]">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#111]">
                          <tr className="text-left text-[#666] border-b border-[#2a2a2a]">
                            <th className="py-2 px-3">#</th>
                            <th className="py-2 px-3">Instrument</th>
                            <th className="py-2 px-3">Period</th>
                            <th className="py-2 px-3 text-right">Return %</th>
                            <th className="py-2 px-3 text-right">Max DD %</th>
                            <th className="py-2 px-3 text-right">Volatility %</th>
                            <th className="py-2 px-3 text-center">Trend</th>
                            <th className="py-2 px-3 text-right">Days</th>
                            <th className="py-2 px-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedInstruments.map((inst, index) => (
                            <tr key={inst.id} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a]">
                              <td className="py-2 px-3 text-[#666]">{index + 1}</td>
                              <td className="py-2 px-3 text-white font-mono text-xs">{inst.id}</td>
                              <td className="py-2 px-3 text-[#888] text-xs">{inst.start_date} to {inst.end_date}</td>
                              <td className={`py-2 px-3 text-right font-bold ${inst.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {inst.total_return_pct >= 0 ? '+' : ''}{inst.total_return_pct.toFixed(2)}%
                              </td>
                              <td className="py-2 px-3 text-right text-red-400 text-xs">{inst.max_drawdown_pct.toFixed(2)}%</td>
                              <td className="py-2 px-3 text-right text-[#888] text-xs">{inst.volatility_ann_pct.toFixed(1)}%</td>
                              <td className="py-2 px-3 text-center">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  inst.trend === 'bullish' ? 'bg-green-500/10 text-green-400'
                                    : inst.trend === 'bearish' ? 'bg-red-500/10 text-red-400'
                                    : 'bg-yellow-500/10 text-yellow-400'
                                }`}>
                                  {inst.trend}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right text-[#888] text-xs">{inst.trading_days}</td>
                              <td className="py-2 px-3">
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => setChartInstrument(inst.id)}
                                    className="text-xs px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
                                    title="View price chart"
                                  >
                                    <LineChartIcon size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedInstrument(inst.id)
                                      setActiveTab('backtest')
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                                  >
                                    Backtest
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )
          })()}

          {/* Empty State */}
          {/* Instrument Chart Modal */}
          {chartInstrument && (
            <InstrumentChartModal
              instrumentId={chartInstrument}
              onClose={() => setChartInstrument(null)}
            />
          )}

          {!marketAnalysis && !marketAnalysisLoading && !marketAnalysisError && (
            <div className="card">
              <div className="text-center py-12">
                <BarChart3 size={48} className="mx-auto text-[#333] mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Market Regime Analysis</h3>
                <p className="text-sm text-[#666] max-w-md mx-auto mb-4">
                  Analyze all instruments to identify the most bullish and bearish market conditions
                  for backtesting validation across different regimes.
                </p>
                <button
                  onClick={() => fetchMarketAnalysis(true)}
                  className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 mx-auto"
                >
                  <BarChart3 size={16} />
                  Run Analysis
                </button>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  )
}

interface MetricCardProps {
  title: string
  value: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  color: 'green' | 'red' | 'yellow' | 'blue'
}

function MetricCard({ title, value, icon: Icon, color }: MetricCardProps) {
  const colorClasses = {
    green: 'bg-green-500/10 text-green-400',
    red: 'bg-red-500/10 text-red-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    blue: 'bg-blue-500/10 text-blue-400',
  }

  return (
    <div className="card flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-xs text-[#666] uppercase">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
      </div>
    </div>
  )
}
